/**
 * Mordeczka.eu Admin - Main Layout with Ant Design
 */

import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Layout as AntLayout,
  Menu,
  theme,
  Button,
  Space,
  Typography,
} from "antd";
import type { MenuProps } from "antd";
import {
  DashboardOutlined,
  BookOutlined,
  VideoCameraOutlined,
  GlobalOutlined,
  TagsOutlined,
  TagOutlined,
  AppstoreOutlined,
  ClusterOutlined,
  EditOutlined,
  CloudServerOutlined,
  UserOutlined,
  IdcardOutlined,
  BankOutlined,
  VideoCameraAddOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TranslationOutlined,
  ShopOutlined,
  SettingOutlined,
  TeamOutlined,
  PlusOutlined,
  LogoutOutlined,
  ShoppingOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { Watch, SprayCan, Tv, Gamepad2 } from "lucide-react";
import { useAuthStore } from "../stores/authStore";

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: string,
  icon?: React.ReactNode,
  children?: MenuItem[],
): MenuItem {
  return { key, icon, children, label } as MenuItem;
}

const menuItems: MenuItem[] = [
  getItem(
    <span style={{ fontWeight: 600 }}>Moja biblioteka</span>,
    "my-library-group",
    <BookOutlined />,
    [
      getItem("Dashboard", "/", <DashboardOutlined />),
      getItem("Wpisy", "/my-library/entries", <BookOutlined />),
      getItem("Statystyki", "/my-library/stats", <BarChartOutlined />),
      getItem("Dodaj...", "/my-library/add", <PlusOutlined />),
    ],
  ),
  getItem("Dzieła", "works-group", <AppstoreOutlined />, [
    getItem("Panel główny", "/dashboard", <DashboardOutlined />),
    getItem("Książki", "/books", <BookOutlined />),
    getItem("Filmy", "/movies", <VideoCameraOutlined />),
    getItem("Seriale", "/series", <Tv size="1em" />),
  ]),
  getItem("Słowniki", "dict-group", <TagsOutlined />, [
    getItem("Pisarze", "/authors", <UserOutlined />),
    getItem("Gatunki filmowe", "/genres", <ClusterOutlined />),
    getItem("Gatunki literackie", "/literary-genres", <EditOutlined />),
    getItem("Języki", "/languages", <TranslationOutlined />),
    getItem("Kina", "/cinemas", <ShopOutlined />),
    getItem("Kraje", "/nationalities", <GlobalOutlined />),
    getItem(
      "Platformy streamingowe",
      "/streaming-platforms",
      <CloudServerOutlined />,
    ),
    getItem("Reżyserzy", "/directors", <VideoCameraAddOutlined />),
    getItem("Tagi", "/tags", <TagOutlined />),
    getItem("Wydawnictwa", "/publishers", <BankOutlined />),
    getItem("Typy abonamentów", "/subscription-types", <TagOutlined />),
    getItem("Biblioteka obrazków", "image-library-group", <AppstoreOutlined />, [
      getItem("Kategorie obrazków", "/image-categories", <TagOutlined />),
      getItem("Obrazki", "/images", <AppstoreOutlined />),
    ]),
  ]),
  getItem("Zakupy", "purchases-group", <ShoppingOutlined />, [
    getItem("Lista zakupów", "/purchases", <ShoppingOutlined />),
    getItem("Gry", "/purchases?item_type=game", <Gamepad2 size={14} />),
    getItem("Marki zegarków", "/watch-brands", <Watch size={14} />),
    getItem("Marki perfum", "/perfume-brands", <SprayCan size={14} />),
  ]),
  getItem("Administracja", "admin-group", <SettingOutlined />, [
    getItem("Użytkownicy", "/users", <TeamOutlined />),
    getItem("Role", "/roles", <IdcardOutlined />),
  ]),
  getItem("System", "system-group", <CloudServerOutlined />, [
    getItem("Stan systemu", "/system/status", <CloudServerOutlined />),
    getItem(
      "Statystyki odwiedzin",
      "/system/traffic",
      <BarChartOutlined />,
    ),
  ]),
];

export function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const { user, logout } = useAuthStore();

  const handleMenuClick: MenuProps["onClick"] = (e) => {
    navigate(e.key);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Find open keys based on current path
  const getOpenKeys = () => {
    if (
      location.pathname === "/" ||
      location.pathname.startsWith("/my-library")
    ) {
      return ["my-library-group"];
    }
    if (
      ["/dashboard", "/books", "/movies", "/series"].some((p) =>
        location.pathname.startsWith(p),
      )
    ) {
      return ["works-group"];
    }

    if (
      [
        "/languages",
        "/tags",
        "/genres",
        "/literary-genres",
        "/streaming-platforms",
        "/authors",
        "/publishers",
        "/directors",
        "/nationalities",
        "/cinemas",
        "/subscription-types",
        "/image-categories",
        "/images",
      ].includes(location.pathname)
    ) {
      if (
        location.pathname === "/image-categories" ||
        location.pathname === "/images"
      ) {
        return ["dict-group", "image-library-group"];
      }
      return ["dict-group"];
    }
    if (["/users", "/roles"].includes(location.pathname)) {
      return ["admin-group"];
    }
    if (location.pathname.startsWith("/system")) {
      return ["system-group"];
    }
    if (
      ["/purchases", "/watch-brands", "/perfume-brands"].includes(
        location.pathname,
      )
    ) {
      return ["purchases-group"];
    }
    return [];
  };

  return (
    <AntLayout
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
      }}
    >
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{ background: colorBgContainer }}
        width={240}
      >
        <div
          style={{
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: "1px solid #f0f0f0",
            margin: "0 16px",
          }}
        >
          <span
            onClick={() => navigate("/")}
            style={{
              fontSize: collapsed ? 16 : 20,
              fontWeight: 700,
              color: "#1677ff",
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            {collapsed ? "M" : "Mordeczka Admin"}
          </span>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[
            location.pathname === "/purchases" &&
            new URLSearchParams(location.search).get("item_type") === "game"
              ? "/purchases?item_type=game"
              : location.pathname,
          ]}
          defaultOpenKeys={getOpenKeys()}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ border: "none", marginTop: 8 }}
        />
      </Sider>
      <AntLayout>
        <Header
          style={{
            padding: "0 24px",
            background: colorBgContainer,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <div>
            {collapsed ? (
              <MenuUnfoldOutlined
                onClick={() => setCollapsed(false)}
                style={{ fontSize: 18, cursor: "pointer" }}
              />
            ) : (
              <MenuFoldOutlined
                onClick={() => setCollapsed(true)}
                style={{ fontSize: 18, cursor: "pointer" }}
              />
            )}
          </div>
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              v{typeof __BUILD_DATE__ !== "undefined"
                ? new Date(__BUILD_DATE__).toLocaleDateString("pl-PL")
                : "dev"}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, margin: "0 4px" }}>|</Text>
            <Text type="secondary">
              {user?.display_name || user?.email || "Użytkownik"}
            </Text>
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              Wyloguj
            </Button>
          </Space>
        </Header>
        <Content
          style={{
            margin: 24,
            padding: 24,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            minHeight: 280,
          }}
        >
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
}

export default Layout;
